import React, { useState, useEffect, useContext } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import Checkbox from "@mui/material/Checkbox";
import { createData, fetchOnlyData } from "../../utility/adminApi";
import { notify } from "../../components/toastify";
import loader from "../../assets/loader.webp";
import { ProfileDetailsContext } from "../../context/ProfileDetailsProvider";
import UpgradeModal from "./UpgradeModal";

function BroadcastModal({ isOpen, onClose, customers }) {
  const { profileDetails } = useContext(ProfileDetailsContext);
  const [message, setMessage] = useState("");
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [sendOption, setSendOption] = useState("now"); // "now", "later", "recurring"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("weekly"); // "weekly", "monthly", "yearly"
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Tabs state
  const [activeTab, setActiveTab] = useState("send"); // "send" or "history"
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessage("");
      setIsHtmlMode(false);
      setSelectedCustomers([]);
      setSendOption("now");
      setScheduledDate("");
      setScheduledTime("");
      setRecurringFrequency("weekly");
      setSelectAll(false);
      setActiveTab("send");
    }
  }, [isOpen]);

  // Handle select all checkbox
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedCustomers(customers.map((customer) => customer.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  // Handle individual customer checkbox
  const handleCustomerToggle = (customerId) => {
    setSelectedCustomers((prev) => {
      if (prev.includes(customerId)) {
        const updated = prev.filter((id) => id !== customerId);
        setSelectAll(updated.length === customers.length);
        return updated;
      } else {
        const updated = [...prev, customerId];
        setSelectAll(updated.length === customers.length);
        return updated;
      }
    });
  };

  // Check if all customers are selected
  useEffect(() => {
    if (customers.length > 0) {
      setSelectAll(selectedCustomers.length === customers.length && customers.length > 0);
    }
  }, [selectedCustomers, customers]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      notify("Please enter a message", "error");
      return;
    }

    // Check plan before allowing broadcast (must be "pro" plan)
    // Since we don't have profile data/context here, you might want to pass plan info as a prop, or use global state (context) if available.
    // Example with context provider access (assumes profileDetails in context):

    // (If you do NOT have profileDetails available, you can delete/comment this check)
    // If you want to enforce the "pro" check, you could do so here:
    if (typeof profileDetails?.subscription?.plan === "string" &&
        profileDetails.subscription.plan.toLowerCase() !== "pro") {
      notify("Broadcasts are only available with the Pro plan. Please upgrade to access this feature.", "error");
      setIsUpgradeModalOpen(true);
      return;
    }

    if (selectedCustomers.length === 0) {
      notify("Please select at least one customer", "error");
      return;
    }

    if (sendOption === "later" && !scheduledDate) {
      notify("Please select a date for scheduled sending", "error");
      return;
    }

    if (sendOption === "recurring" && !scheduledDate) {
      notify("Please select a start date for recurring broadcast", "error");
      return;
    }

    setLoading(true);

    try {
      const broadcastData = {
        message: message,
        message_type: isHtmlMode ? "html" : "plain",
        customer_ids: selectedCustomers,
        send_option: sendOption,
        scheduled_date: sendOption !== "now" ? scheduledDate : null,
        scheduled_time: sendOption !== "now" ? scheduledTime : null,
        recurring_frequency: sendOption === "recurring" ? recurringFrequency : null,
      };

      const response = await createData("broadcasts/store", broadcastData);

      if (response?.error === false) {
        notify(response?.message || "Broadcast created successfully", "success");
        onClose();
      }
    } catch (error) {
      console.error("Error creating broadcast:", error);
      notify(
        error?.response?.data?.message || "Failed to create broadcast",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch broadcast history (for history tab)
  useEffect(() => {
    const fetchBroadcastHistory = async () => {
      try {
        setHistoryLoading(true);
        const response = await fetchOnlyData("broadcasts/history");
        if (Array.isArray(response)) {
          setBroadcastHistory(response);
        } else if (Array.isArray(response?.data)) {
          setBroadcastHistory(response.data);
        } else {
          setBroadcastHistory([]);
        }
      } catch (error) {
        console.error("Error fetching broadcast history:", error);
        setBroadcastHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    if (activeTab === "history" && isOpen) {
      fetchBroadcastHistory();
    }
  }, [activeTab, isOpen]);

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-0 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header with Tabs */}
        <div className="flex justify-between items-center px-6 pt-6 pb-2 border-b border-gray-200">
          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded-t-lg text-lg font-semibold focus:outline-none transition ${
                activeTab === "send"
                  ? "bg-white border-b-2 border-blue-500 text-blue-700"
                  : "bg-transparent text-gray-400"
              }`}
              onClick={() => setActiveTab("send")}
              type="button"
            >
              Send Broadcast
            </button>
            <button
              className={`px-4 py-2 rounded-t-lg text-lg font-semibold focus:outline-none transition ${
                activeTab === "history"
                  ? "bg-white border-b-2 border-blue-500 text-blue-700"
                  : "bg-transparent text-gray-400"
              }`}
              onClick={() => setActiveTab("history")}
              type="button"
            >
              History
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CancelIcon className="cursor-pointer text-black" />
          </button>
        </div>
        <div className="p-6 pt-4">
          {activeTab === "send" && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Message Editor */}
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isHtmlMode}
                      onChange={(e) => setIsHtmlMode(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">HTML Mode</span>
                  </label>
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    isHtmlMode
                      ? "Enter your HTML message..."
                      : "Enter your message..."
                  }
                  className="border-2 border-gray-300 p-3 rounded-md focus:border-teal-500 focus:outline-none min-h-[150px] font-mono text-sm"
                  required
                />
                {isHtmlMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    You can use HTML tags for formatting (e.g., &lt;b&gt;, &lt;i&gt;, &lt;p&gt;, &lt;a&gt;)
                  </p>
                )}
              </div>

              {/* Customer Selection */}
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Select Customers <span className="text-red-500">*</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      size="small"
                    />
                    <span className="text-sm text-gray-600">Select All</span>
                  </label>
                </div>
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                  {customers.length === 0 ? (
                    <p className="text-sm text-gray-400 italic p-4 text-center">
                      No customers available
                    </p>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {customers.map((customer) => (
                        <label
                          key={customer.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedCustomers.includes(customer.id)}
                            onChange={() => handleCustomerToggle(customer.id)}
                            size="small"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {customer.name}
                            </p>
                            {customer.preference && (
                              <p className="text-xs text-gray-500">
                                {customer.preference}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCustomers.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedCustomers.length} customer(s) selected
                  </p>
                )}
              </div>

              {/* Send Option */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Send Broadcast <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sendOption"
                      value="now"
                      checked={sendOption === "now"}
                      onChange={(e) => setSendOption(e.target.value)}
                      className="cursor-pointer"
                    />
                    <span className="text-sm">Send Now</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sendOption"
                      value="later"
                      checked={sendOption === "later"}
                      onChange={(e) => setSendOption(e.target.value)}
                      className="cursor-pointer"
                    />
                    <span className="text-sm">Send Later</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sendOption"
                      value="recurring"
                      checked={sendOption === "recurring"}
                      onChange={(e) => setSendOption(e.target.value)}
                      className="cursor-pointer"
                    />
                    <span className="text-sm">Recurring</span>
                  </label>
                </div>

                {/* Scheduled Date/Time for Later */}
                {sendOption === "later" && (
                  <div className="mt-3 flex flex-col gap-2 pl-6">
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600 mb-1">
                        Scheduled Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600 mb-1">
                        Scheduled Time (Optional)
                      </label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* Recurring Options */}
                {sendOption === "recurring" && (
                  <div className="mt-3 flex flex-col gap-2 pl-6">
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600 mb-1">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600 mb-1">
                        Frequency
                      </label>
                      <select
                        value={recurringFrequency}
                        onChange={(e) => setRecurringFrequency(e.target.value)}
                        className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-300">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !message.trim() || selectedCustomers.length === 0}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <img src={loader} alt="loading" className="w-5" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    `Send ${sendOption === "now" ? "Now" : sendOption === "later" ? "Later" : "Recurring"}`
                  )}
                </button>
              </div>
            </form>
          )}
          {activeTab === "history" && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Broadcast History</h3>
              {historyLoading ? (
                <div className="flex justify-center items-center py-10">
                  <img src={loader} alt="loading" className="w-7" />
                  <span className="ml-3 text-gray-500">Loading history...</span>
                </div>
              ) : broadcastHistory.length === 0 ? (
                <div className="text-gray-400 p-6 text-center">
                  No broadcast history found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead>
                      <tr className="bg-gray-100 text-sm text-gray-700">
                        <th className="py-2 px-3 border-b">Date</th>
                        <th className="py-2 px-3 border-b">Type</th>
                        <th className="py-2 px-3 border-b">Recipients</th>
                        <th className="py-2 px-3 border-b">Message</th>
                        <th className="py-2 px-3 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {broadcastHistory.map((item) => (
                        <tr key={item.id} className="border-t text-gray-700">
                          <td className="py-2 px-3 text-xs">
                            {item.created_at
                              ? new Date(item.created_at).toLocaleString()
                              : "-"}
                          </td>
                          <td className="py-2 px-3 text-xs capitalize">
                            {item.message_type}
                            {item.recurring_frequency
                              ? ` (${item.recurring_frequency})`
                              : ""}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            {Array.isArray(item.customer_names) &&
                            item.customer_names.length > 0
                              ? item.customer_names.join(", ")
                              : Array.isArray(item.customer_ids)
                              ? item.customer_ids.length
                              : 0}
                          </td>
                          <td className="py-2 px-3 text-xs max-w-xs break-words">
                            {item.message_type === "html" ? (
                              <span
                                dangerouslySetInnerHTML={{ __html: item.message }}
                              />
                            ) : (
                              item.message
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            {item.status ? item.status : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    
    <UpgradeModal
      isOpen={isUpgradeModalOpen}
      onClose={() => setIsUpgradeModalOpen(false)}
    />
    </>
  );
}

export default BroadcastModal;